import { Button } from 'tio-ui/components/buttons';
import { fn, hash, uniqueId } from '@ember/helper';
import { Input, Textarea, RadioGroup, Select } from 'tio-ui/components/forms';
import { on } from '@ember/modifier';
import { PhTrash, PhPlus } from 'ember-phosphor-icons';
import { Section, VStack } from 'tio-ui/components/layout';
import { Switch } from 'tio-ui/components/forms';
import { t } from 'ember-intl';
import { eq, gt } from 'tio-ui/utilities';
import { cached, tracked } from '@glimmer/tracking';
import Component from '@glimmer/component';
import FieldOptionsEditDialog from '../field-options/edit-dialog.gts';
import TioClickableText from '../../tio/clickable-text.gts';
import type { CustomFieldSignature } from 'tio-common/types/tuition-assistance';
import type TasProgramTemplateModel from '../../../models/tas-program-template';
import type TasFieldOptionModel from '../../../models/tas-field-option';
import {
  getSortedOptionsByFieldName,
  type CustomFieldId,
} from '../../../utils/tuition-assistance/fields.ts';

export interface TasProgramTemplateReviewCustomRowSig {
  Args: {
    model: CustomFieldSignature;
    removeCustomRow: () => void;
    updateCustomInputType: (value: CustomFieldSignature['input']) => void;
    updateCustomInputSubtype?: (subtype: NonNullable<CustomFieldSignature['inputType']>) => void;
    programTemplate: TasProgramTemplateModel;
    fieldOptions?: TasFieldOptionModel[];
    index?: number;
    didUpdateFieldOptions?: () => void;
  };
}

export default class TasProgramTemplateReviewCustomRow extends Component<TasProgramTemplateReviewCustomRowSig> {
  @tracked isShowingSelectOptionsDialog = false;

  getSortedOptionsByFieldName = getSortedOptionsByFieldName;

  updateOrdinal = (value: string) => {
    this.args.model.ordinal = parseInt(value);
  };

  inputTypeOptions = [
    { key: 'string', label: 'String' },
    { key: 'currency', label: 'Currency' },
    { key: 'date', label: 'Date' },
  ];

  handleInputTypeChange = (value: CustomFieldSignature['input']) => {
    this.args.updateCustomInputType(value);
    if (value === 'radio' && this.args.model.options.length === 0) {
      // Automatically create two options: "Yes" and "No"
      this.args.model.options = [
        { label: 'Yes', value: 'yes' },
        { label: 'No', value: 'no' },
      ];
    }
    if (value === 'select') {
      this.args.model.value_as_field_option = true;
    } else {
      this.args.model.value_as_field_option = false;
    }
  };

  addOption = () => {
    // For reactivity, we need to replace the entire array
    // Default to "Yes" and "No" options if this is the first time adding options
    if (this.args.model.options.length === 0) {
      this.args.model.options = [
        { label: 'Yes', value: 'yes' },
        { label: 'No', value: 'no' },
      ];
    } else {
      // If already has options, add a generic new option
      const newIndex = this.args.model.options.length;
      this.args.model.options = [
        ...this.args.model.options,
        {
          label: '',
          value: `option_${newIndex + 1}`,
        },
      ];
    }
  };

  removeOption = (index: number) => {
    // For reactivity, we need to replace the entire array
    const newOptions = [...this.args.model.options];
    newOptions.splice(index, 1);
    // Regenerate values to maintain consistency after removal
    newOptions.forEach((option, idx) => {
      option.value = `option_${idx + 1}`;
    });
    this.args.model.options = newOptions;
  };

  updateOptionLabel = (index: number, newLabel: string) => {
    const option = this.args.model.options[index];
    if (option) {
      option.label = newLabel;
      // Auto-generate value based on label or maintain index-based approach
      option.value =
        newLabel
          .toLowerCase()
          .replace(/[^a-z0-9\s]/g, '') // Remove special characters
          .replace(/\s+/g, '_') // Replace spaces with underscores
          .trim() || `option_${index + 1}`; // Fallback to index if label becomes empty
    }
  };

  updateCustomInputSubtype = (value: string) => {
    this.args.updateCustomInputSubtype?.(value as NonNullable<CustomFieldSignature['inputType']>);
  };

  toggleSelectOptionsDialog = () => {
    this.isShowingSelectOptionsDialog = !this.isShowingSelectOptionsDialog;
  };

  handleSelectOptionsDialogSubmit = () => {
    this.isShowingSelectOptionsDialog = false;
    this.args.didUpdateFieldOptions?.();
  };

  handleSelectOptionsDialogCancel = () => {
    this.isShowingSelectOptionsDialog = false;
  };

  handleSelectOptionsDialogClose = (newOptions?: Array<{ label: string }>) => {
    this.isShowingSelectOptionsDialog = false;
    if (newOptions) {
      const normalizedOptions = newOptions.map((option, index) => {
        const label = option.label || `Option ${index + 1}`;
        const value =
          label
            .toLowerCase()
            .replace(/[^a-z0-9\s]/g, '')
            .replace(/\s+/g, '_')
            .trim() || `option_${index + 1}`;
        return { label, value };
      });
      // Trigger reactivity by replacing the entire array
      this.args.model.options = [...normalizedOptions];
    }
  };

  get fieldName() {
    // For custom fields, use the backend-assigned ID instead of name
    return this.args.model?.id as CustomFieldId;
  }

  @cached
  get fieldOptions() {
    return this.args.model?.value_as_field_option && this.fieldName
      ? getSortedOptionsByFieldName(this.fieldName, this.args.programTemplate.tasFieldOptions).map(
          (option) => option.value
        )
      : [];
  }

  @cached
  get sortedFieldOptions(): typeof this.args.programTemplate.tasFieldOptions {
    // If there's no fieldName (custom fields may not have one), return empty array.
    if (!this.fieldName) return [];
    return getSortedOptionsByFieldName(this.fieldName, this.args.programTemplate.tasFieldOptions);
  }

  <template>
    <Section>
      <:body>
        <div class="col-span-6 grid grid-cols-10 gap-8 items-start my-4">
          {{! Order }}
          <div class="col-span-1">
            <Input
              @value={{String @model.ordinal}}
              @onChange={{this.updateOrdinal}}
              class="text-sm"
              @classes={{hash base="w-14 mx-auto"}}
              type="number"
            />
          </div>

          {{! Field Label }}
          <div class="col-span-3">
            <p class="text-xs">{{this.fieldName}}</p>
            <Textarea
              @value={{@model.label}}
              @onChange={{fn (mut @model.label)}}
              placeholder="Enter label for new field..."
            />
          </div>

          {{! Type Selection }}
          <div class="col-span-2 pl-4">
            <VStack>
              {{#let (uniqueId) as |uniqueId|}}
                <RadioGroup
                  @name="input-type-radio-{{uniqueId}}"
                  @value={{@model.input}}
                  @onChange={{this.handleInputTypeChange}}
                  as |Radio|
                >
                  <Radio @value="textarea" @label="Textarea" />
                  <Radio @value="input" @label="Input" />
                  <Radio @value="radio" @label="Radio" />
                  <Radio @value="select" @label="Select" />
                </RadioGroup>
              {{/let}}
            </VStack>

          </div>
          <div class="col-span-2">
            {{#if (eq @model.input "input")}}
              <div class="mt-2 p-2">
                <div class="text-sm font-medium mb-2">Input Type:</div>
                {{#if @updateCustomInputSubtype}}
                  <Select
                    @items={{this.inputTypeOptions}}
                    @selectionMode="single"
                    @selectedKey={{@model.inputType}}
                    @onAction={{this.updateCustomInputSubtype}}
                    @placeholder="Select input type..."
                  >
                    <:item as |o|>
                      <o.Item @key={{o.item.key}}>
                        {{o.item.label}}
                      </o.Item>
                    </:item>
                  </Select>
                {{else}}
                  <div class="text-red-500 text-xs">Error: updateCustomInputSubtype method not
                    provided</div>
                {{/if}}
              </div>
            {{/if}}

            {{#if (eq @model.input "radio")}}
              <div class="mt-4 space-y-2">
                <div class="text-sm font-medium">Options:</div>
                {{#each @model.options as |option index|}}
                  <div class="flex gap-2 items-center">
                    <Input
                      @value={{option.label}}
                      @onChange={{fn this.updateOptionLabel index}}
                      placeholder="Enter option label..."
                      class="flex-1"
                    />
                    <Button
                      @appearance="outlined"
                      @intent="danger"
                      @size="sm"
                      {{on "click" (fn this.removeOption index)}}
                    >
                      <PhTrash @size="16" />
                    </Button>
                  </div>
                {{/each}}
                <Button
                  @appearance="outlined"
                  @intent="primary"
                  @size="sm"
                  {{on "click" this.addOption}}
                >
                  <PhPlus @size="16" />
                  Add Option
                </Button>
              </div>
            {{/if}}

            {{#if (eq @model.input "select")}}
              <div class="mt-4">
                <div class="text-sm font-medium mb-2">Options:</div>
                <ul class="list-disc list-inside mb-2">
                  {{#each this.fieldOptions as |option|}}
                    <li class="text-sm text-gray-600 truncate">{{option}}</li>
                  {{/each}}
                </ul>

                <TioClickableText
                  @onClick={{this.toggleSelectOptionsDialog}}
                  @textClass="text-sm tio-anchor"
                  class="text-center mx-1"
                >
                  {{if
                    @model.options.length
                    (t "common.tuition_assistance.program_template_review.edit_options")
                    (t "common.tuition_assistance.program_template_review.add_options")
                  }}
                </TioClickableText>
                {{#if @model.options.length}}
                  <div class="mt-2 text-xs text-gray-600">
                    {{@model.options.length}}
                    option{{if (gt @model.options.length 1) "s"}}
                    configured
                  </div>
                {{/if}}
              </div>
            {{/if}}
          </div>
          {{! Visible / Required Switches and Delete Button }}
          <div class="col-span-2 flex justify-between no-wrap">
            <div class="flex flex-col gap-4">
              {{! stack switches with spacing }}
              <div>
                <span class="block text-sm font-medium text-gray-700 mb-1">
                  {{t "common.visible"}}
                </span>
                <div class="flex gap-2 items-center">
                  <label>{{t "common.no"}}</label>
                  <Switch
                    @label={{t "common.yes"}}
                    @intent="success"
                    @isSelected={{@model.visible}}
                    @onChange={{fn (mut @model.visible)}}
                  />
                </div>
              </div>

              <div>
                <span class="block text-sm font-medium text-gray-700 mb-1">
                  {{t "common.required"}}
                </span>
                <div class="flex gap-2 items-center">
                  <label>{{t "common.no"}}</label>
                  <Switch
                    @label={{t "common.yes"}}
                    @intent="success"
                    @isSelected={{@model.required}}
                    @onChange={{fn (mut @model.required)}}
                  />
                </div>
              </div>
            </div>

            <Button
              @appearance="outlined"
              @intent="danger"
              {{on "click" @removeCustomRow}}
              class="ml-4 self-center"
            >
              <PhTrash @size="20" />
            </Button>
          </div>
        </div>
      </:body>
    </Section>

    {{#if this.isShowingSelectOptionsDialog}}
      <FieldOptionsEditDialog
        @programTemplate={{@programTemplate}}
        {{! @glint-expect-error: using model label as field name for custom fields }}
        @fieldName={{this.fieldName}}
        @fieldLabel={{@model.label}}
        @fieldOptions={{this.sortedFieldOptions}}
        @didCancel={{this.handleSelectOptionsDialogCancel}}
        @didSubmit={{this.handleSelectOptionsDialogSubmit}}
      />
    {{/if}}
  </template>
}
